import React, { useMemo, useState } from "react";

// Minimal sortable table that replaces ag-grid on the dacom pages. Columns:
//   { key: string, title: string, type?: "number" | "numericColumn",
//     render?: (row) => ReactNode }
// Rows are plain objects keyed by `column.key`. Numeric columns compare as
// numbers even when values are locale-formatted strings like "12,5".
const parseLocaleNumber = (value) => {
  if (typeof value === "number") return value;
  if (value == null) return NaN;
  // Strip spaces (thousands sep), then normalize comma-decimal to dot-decimal.
  const normalized = String(value).replace(/\s/g, "").replace(/,/g, ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const DataGrid = ({ rows, columns, maxHeight = 400, className = "" }) => {
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const isNumeric = col && (col.type === "number" || col.type === "numericColumn");
    const copy = rows.slice();
    copy.sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      if (isNumeric) {
        av = parseLocaleNumber(av);
        bv = parseLocaleNumber(bv);
      }
      const aMissing = av == null || (typeof av === "number" && Number.isNaN(av));
      const bMissing = bv == null || (typeof bv === "number" && Number.isNaN(bv));
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sort, columns]);

  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );

  return (
    <div className={`dacom-datagrid ${className}`} style={{ maxHeight, overflow: "auto" }}>
      <table className="dacom-datagrid__table">
        <thead>
          <tr>
            {columns.map((col) => {
              const indicator =
                sort.key === col.key ? (sort.dir === "asc" ? " \u25B2" : " \u25BC") : "";
              return (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  {col.title}
                  {indicator}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => (
            <tr key={row.id != null ? row.id : idx}>
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataGrid;
